// ItemsSummaryPage.tsx
import {Database, ViewFilterT, useDataQuery} from '@tnc/shared';
import {useMemo, useState} from 'react';

import {DataTableView, DataTableViewPropsT} from '@/components/dataTableView';
import DefaultTableAvatarImage from '@/components/defaultTableAvatarImage';
import {SearchAndSelectComponent} from '@/components/searchAndSelectComponent';

type ItemsSummaryView = 'items_summary';
type ItemsSummaryRow = Database['public']['Views'][ItemsSummaryView]['Row'];

// Add these types for our selected items
type BrandT = {brand_id: number; brand_name: string};
type MarketT = {market_id: number; market_name: string};
type DistributorT = {distributor_id: number; distributor_name: string};
type RetailerT = {retailer_id: number; retailer_name: string};

function ItemsSummaryPage() {
  // Selected items state
  const [selectedProduct, setSelectedProduct] = useState<{product_id: number; product_name: string | null} | null>(
    null
  );
  const [selectedBrand, setSelectedBrand] = useState<BrandT | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<MarketT | null>(null);
  const [selectedDistributor, setSelectedDistributor] = useState<DistributorT | null>(null);
  const [selectedRetailer, setSelectedRetailer] = useState<RetailerT | null>(null);
  const [selectedInventoryType, setSelectedInventoryType] = useState<'retailer' | 'distributor' | 'pending' | ''>('');

  // Search modal visibility states
  const [isProductSearchVisible, setIsProductSearchVisible] = useState(false);
  const [isBrandSearchVisible, setIsBrandSearchVisible] = useState(false);
  const [isMarketSearchVisible, setIsMarketSearchVisible] = useState(false);
  const [isDistributorSearchVisible, setIsDistributorSearchVisible] = useState(false);
  const [isRetailerSearchVisible, setIsRetailerSearchVisible] = useState(false);

  // Search terms
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [marketSearchTerm, setMarketSearchTerm] = useState('');
  const [distributorSearchTerm, setDistributorSearchTerm] = useState('');
  const [retailerSearchTerm, setRetailerSearchTerm] = useState('');

  // Data Queries
  const {data: products} = useDataQuery({
    table: 'Product',
    columns: ['product_id', 'product_name'],
    searchColumn: 'product_name',
    searchTerm: productSearchTerm,
    enabled: isProductSearchVisible,
  });

  const {data: brands} = useDataQuery({
    table: 'Brand',
    columns: ['brand_id', 'brand_name'],
    searchColumn: 'brand_name',
    searchTerm: brandSearchTerm,
    enabled: isBrandSearchVisible,
  });

  const {data: markets} = useDataQuery({
    table: 'Market',
    columns: ['market_id', 'market_name'],
    searchColumn: 'market_name',
    searchTerm: marketSearchTerm,
    enabled: isMarketSearchVisible,
  });

  const {data: distributors} = useDataQuery({
    table: 'Distributor',
    columns: ['distributor_id', 'distributor_name'],
    searchColumn: 'distributor_name',
    searchTerm: distributorSearchTerm,
    enabled: isDistributorSearchVisible,
  });

  const {data: retailers} = useDataQuery({
    table: 'Retailer',
    columns: ['retailer_id', 'retailer_name'],
    searchColumn: 'retailer_name',
    searchTerm: retailerSearchTerm,
    enabled: isRetailerSearchVisible,
  });

  // Reset functions
  const resetAllFilters = () => {
    setSelectedProduct(null);
    setSelectedBrand(null);
    setSelectedMarket(null);
    setSelectedDistributor(null);
    setSelectedRetailer(null);
    setSelectedInventoryType('');
  };

  /****************************************
   * Filters
   *****************************************/
  // Build filters dynamically based on selected values
  const filters = useMemo<ViewFilterT<ItemsSummaryView>[] | undefined>(() => {
    const f: ViewFilterT<ItemsSummaryView>[] = [];

    // Previous filters remain the same
    if (selectedProduct) {
      f.push({
        column: 'product_id',
        operator: 'eq',
        value: selectedProduct.product_id,
      });
    }

    if (selectedBrand) {
      f.push({
        column: 'brand_id',
        operator: 'eq',
        value: selectedBrand.brand_id,
      });
    }

    // Inventory type filters remain the same
    if (selectedInventoryType === 'retailer') {
      f.push({
        column: 'retailer_inventory',
        operator: 'gt',
        value: 0,
      });
    } else if (selectedInventoryType === 'distributor') {
      f.push({
        column: 'distributor_inventory',
        operator: 'gt',
        value: 0,
      });
    } else if (selectedInventoryType === 'pending') {
      f.push({
        column: 'pending_inventory',
        operator: 'gt',
        value: 0,
      });
    }

    // Updated JSON array filters using containedBy
    if (selectedMarket) {
      f.push({
        column: 'markets',
        operator: 'containedBy',
        value: {market_id: selectedMarket.market_id},
      });
    }

    if (selectedDistributor) {
      f.push({
        column: 'distributors',
        operator: 'containedBy',
        value: {distributor_id: selectedDistributor.distributor_id},
      });
    }

    if (selectedRetailer) {
      f.push({
        column: 'retailers',
        operator: 'containedBy',
        value: {retailer_id: selectedRetailer.retailer_id},
      });
    }

    return f.length > 0 ? f : undefined;
  }, [selectedProduct, selectedBrand, selectedMarket, selectedDistributor, selectedRetailer, selectedInventoryType]);

  /****************************************
   * Columns
   *****************************************/
  // Define columns for the table
  const columns = useMemo<DataTableViewPropsT<'items_summary', ItemsSummaryRow>['columnsDisplay']>(
    () => [
      {
        header: 'Product ID',
        field: 'product_id',
        render: row => (
          <div className="flex min-w-[150px] gap-2">
            <DefaultTableAvatarImage src={row.product_logo || row.brand_logo} alt="Product Logo" />
            <div className="flex flex-col">
              <span>{row.product_name}</span>
              <span className="text-muted text-truncate text-gray text-xs text-textmuted">{row.brand_name}</span>
            </div>
          </div>
        ),
      },
      {
        header: 'Pending Inventory',
        field: 'pending_inventory',
        render: row => (
          <span className={`${row.pending_inventory && row.pending_inventory > 0 ? 'text-success' : 'text-danger'}`}>
            {row.pending_inventory}
          </span>
        ),
      },
      {
        header: 'Retailer Inventory',
        field: 'retailer_inventory',
        render: row => (
          <span className={`${row.retailer_inventory && row.retailer_inventory > 0 ? 'text-success' : 'text-danger'}`}>
            {row.retailer_inventory}
          </span>
        ),
      },
      {
        header: 'Distributor Inventory',
        field: 'distributor_inventory',
        render: row => (
          <span
            className={`${row.distributor_inventory && row.distributor_inventory > 0 ? 'text-success' : 'text-danger'}`}
          >
            {row.distributor_inventory}
          </span>
        ),
      },
      {
        header: 'Total Items',
        field: 'total_items',
        render: row => <span>{row.total_items}</span>,
      },
      {
        header: 'Markets',
        field: 'markets',
        render: row => {
          // markets is a JSON array of objects: [{market_id, market_name, count}, ...]
          if (!row.markets) return '-';
          const arr = row.markets as {market_id: number; market_name: string; count: number}[];
          return (
            <div className="flex flex-wrap gap-2">
              {arr.map(m => (
                <span key={m.market_id} className="badge bg-info text-white">
                  {m.market_name} ({m.count})
                </span>
              ))}
            </div>
          );
        },
      },
      {
        header: 'Distributors',
        field: 'distributors',
        render: row => {
          if (!row.distributors) return '-';
          const arr = row.distributors as {distributor_id: number; distributor_name: string; count: number}[];
          return (
            <div className="flex flex-wrap gap-2">
              {arr.map(d => (
                <span key={d.distributor_id} className="badge bg-primary text-white">
                  {d.distributor_name} ({d.count})
                </span>
              ))}
            </div>
          );
        },
      },
      {
        header: 'Retailers',
        field: 'retailers',
        render: row => {
          if (!row.retailers) return '-';
          const arr = row.retailers as {retailer_id: number; retailer_name: string; count: number}[];
          return (
            <div className="flex flex-wrap gap-2">
              {arr.map(r => (
                <span key={r.retailer_id} className="badge bg-success text-white">
                  {r.retailer_name} ({r.count})
                </span>
              ))}
            </div>
          );
        },
      },
    ],
    []
  );

  /****************************************
   * Render
   *****************************************/
  return (
    <div className="p-4">
      <h1 className="mb-4 text-xl font-bold">Items Summary</h1>

      {/* Reset Filters Button */}
      <div className="mb-4 flex justify-end">
        <button onClick={resetAllFilters} className="ti-btn ti-btn-danger-full">
          Reset All Filters
        </button>
      </div>

      <div className="mb-4 grid grid-cols-12 gap-6">
        {/* Product Search */}
        <div className="col-span-12 sm:col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4">
          <label className="form-label">Product</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="form-control"
              value={selectedProduct?.product_name ?? ''}
              readOnly
              placeholder="Select product..."
              onClick={() => setIsProductSearchVisible(true)}
            />
            {selectedProduct && (
              <button onClick={() => setSelectedProduct(null)} className="ti-btn ti-btn-danger-full !p-2">
                ×
              </button>
            )}
          </div>
        </div>

        {/* Brand Search */}
        <div className="col-span-12 sm:col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4">
          <label className="form-label">Brand</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="form-control"
              value={selectedBrand?.brand_name || ''}
              readOnly
              placeholder="Select brand..."
              onClick={() => setIsBrandSearchVisible(true)}
            />
            {selectedBrand && (
              <button onClick={() => setSelectedBrand(null)} className="ti-btn ti-btn-danger-full !p-2">
                ×
              </button>
            )}
          </div>
        </div>

        {/* Market Search */}
        <div className="col-span-12 sm:col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4">
          <label className="form-label">Market</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="form-control"
              value={selectedMarket?.market_name || ''}
              readOnly
              placeholder="Select market..."
              onClick={() => setIsMarketSearchVisible(true)}
            />
            {selectedMarket && (
              <button onClick={() => setSelectedMarket(null)} className="ti-btn ti-btn-danger-full !p-2">
                ×
              </button>
            )}
          </div>
        </div>

        {/* Distributor Search */}
        <div className="col-span-12 sm:col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4">
          <label className="form-label">Distributor</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="form-control"
              value={selectedDistributor?.distributor_name || ''}
              readOnly
              placeholder="Select distributor..."
              onClick={() => setIsDistributorSearchVisible(true)}
            />
            {selectedDistributor && (
              <button onClick={() => setSelectedDistributor(null)} className="ti-btn ti-btn-danger-full !p-2">
                ×
              </button>
            )}
          </div>
        </div>

        {/* Retailer Search */}
        <div className="col-span-12 sm:col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4">
          <label className="form-label">Retailer</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="form-control"
              value={selectedRetailer?.retailer_name || ''}
              readOnly
              placeholder="Select retailer..."
              onClick={() => setIsRetailerSearchVisible(true)}
            />
            {selectedRetailer && (
              <button onClick={() => setSelectedRetailer(null)} className="ti-btn ti-btn-danger-full !p-2">
                ×
              </button>
            )}
          </div>
        </div>

        {/* Inventory type filter */}
        <div className="col-span-12 sm:col-span-12 md:col-span-6 lg:col-span-6 xl:col-span-4">
          <label className="form-label">Inventory Type</label>
          <select
            className="form-select !py-[0.59rem]"
            value={selectedInventoryType}
            onChange={e => setSelectedInventoryType(e.target.value as 'retailer' | 'distributor' | 'pending' | '')}
          >
            <option value="">All</option>
            <option value="retailer">Retailer</option>
            <option value="distributor">Distributor</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Search and Select Components */}
      <SearchAndSelectComponent<{
        product_id: number;
        product_name: string | null;
      }>
        isVisible={isProductSearchVisible}
        onClose={() => setIsProductSearchVisible(false)}
        onSelect={item => setSelectedProduct(item)}
        onSearch={setProductSearchTerm}
        results={(products?.data ?? []) as {product_id: number; product_name: string | null}[]}
        title="Select Product"
        mapStructure={{
          displayFields: [
            {title: 'Product Name', field: 'product_name'},
            {title: 'Product ID', field: 'product_id'},
          ],
          valueField: 'product_id',
        }}
      />

      <SearchAndSelectComponent
        isVisible={isBrandSearchVisible}
        onClose={() => setIsBrandSearchVisible(false)}
        onSelect={item => setSelectedBrand(item)}
        onSearch={setBrandSearchTerm}
        results={(brands?.data ?? []) as {brand_id: number; brand_name: string}[]}
        title="Select Brand"
        mapStructure={{
          displayFields: [
            {title: 'Brand Name', field: 'brand_name'},
            {title: 'Brand ID', field: 'brand_id'},
          ],
          valueField: 'brand_id',
        }}
      />

      <SearchAndSelectComponent
        isVisible={isMarketSearchVisible}
        onClose={() => setIsMarketSearchVisible(false)}
        onSelect={item => setSelectedMarket(item)}
        onSearch={setMarketSearchTerm}
        results={(markets?.data ?? []) as {market_id: number; market_name: string}[]}
        title="Select Market"
        mapStructure={{
          displayFields: [
            {title: 'Market Name', field: 'market_name'},
            {title: 'Market ID', field: 'market_id'},
          ],
          valueField: 'market_id',
        }}
      />

      <SearchAndSelectComponent
        isVisible={isDistributorSearchVisible}
        onClose={() => setIsDistributorSearchVisible(false)}
        onSelect={item => setSelectedDistributor(item)}
        onSearch={setDistributorSearchTerm}
        results={(distributors?.data ?? []) as {distributor_id: number; distributor_name: string}[]}
        title="Select Distributor"
        mapStructure={{
          displayFields: [
            {title: 'Distributor Name', field: 'distributor_name'},
            {title: 'Distributor ID', field: 'distributor_id'},
          ],
          valueField: 'distributor_id',
        }}
      />

      <SearchAndSelectComponent
        isVisible={isRetailerSearchVisible}
        onClose={() => setIsRetailerSearchVisible(false)}
        onSelect={item => setSelectedRetailer(item)}
        onSearch={setRetailerSearchTerm}
        results={(retailers?.data ?? []) as {retailer_id: number; retailer_name: string}[]}
        title="Select Retailer"
        mapStructure={{
          displayFields: [
            {title: 'Retailer Name', field: 'retailer_name'},
            {title: 'Retailer ID', field: 'retailer_id'},
          ],
          valueField: 'retailer_id',
        }}
      />

      <DataTableView
        view="items_summary"
        columnsDisplay={columns}
        filters={filters}
        searchColumn="product_name"
        title="Items Summary Results"
        limit={10}
        sortOptions={[
          {
            column: 'total_items',
            direction: 'desc',
            label: 'Total Items Descending',
            default: true,
          },
        ]}
      />
    </div>
  );
}

export default ItemsSummaryPage;
